// ME Reports — data access (S5 / D60). READ-ONLY: every call is a report RPC over
// the existing KPI layer + raw_leads/campaigns/deals. No writes. NB: this is the
// MARKETING/raw-lead/campaign/dialer Reports module (under Marketing) — distinct
// from the top-level whole-spine "Reports" (/kpi), which stays separate.
import { getSupabaseClient } from "../providers/supabase/supabase";

export type Funnel = {
  from: string; to: string; campaign_id: string | null;
  raw_leads: number; contacted: number; conversations: number;
  promoted: number; offers: number; contracts: number;
  range_spend: number | null;
  cost_per_contact: number | null; cost_per_contract: number | null;
};
export type SpendRow = { id: string; period_month: string; amount: number; note: string | null };
export type CampaignPerf = {
  campaign_id: string | null; campaign_name: string; raw_leads: number;
  dials: number; connects: number; conversations: number; conversation_rate: number | null;
  sms: number; mail: number; promoted: number; offers: number; deals: number;
};
export type AgentDay = {
  agent_id: string | null; agent_name: string | null; day: string;
  dials: number; correct_numbers: number; conversations: number; appointments: number;
  talk_time_s: number; conversation_rate: number | null; dials_to_correct: number | null;
};

export async function loadFunnel(from: string, to: string, campaignId: string | null): Promise<Funnel | null> {
  const { data } = await getSupabaseClient().rpc("report_funnel", { p_from: from, p_to: to, p_campaign_id: campaignId });
  return (data as Funnel) ?? null;
}
export async function loadCampaignPerf(from: string, to: string): Promise<CampaignPerf[]> {
  const { data } = await getSupabaseClient().rpc("report_campaign_performance", { p_from: from, p_to: to });
  return ((data as CampaignPerf[]) ?? []);
}
export async function loadCallsByAgent(from: string, to: string, campaignId: string | null): Promise<AgentDay[]> {
  const { data } = await getSupabaseClient().rpc("report_calls_by_agent", { p_from: from, p_to: to, p_campaign_id: campaignId });
  return ((data as AgentDay[]) ?? []);
}
export async function loadCampaignOptions(): Promise<{ id: string; name: string }[]> {
  const { data } = await getSupabaseClient().from("campaigns").select("id,name").eq("audience", "raw_lead").order("name");
  return (data ?? []) as any[];
}

// ---- Marketing spend (admin-entered monthly total; D63) ----------------------
export async function loadSpend(): Promise<SpendRow[]> {
  const { data } = await getSupabaseClient().from("marketing_spend").select("id,period_month,amount,note").order("period_month", { ascending: false });
  return (data ?? []) as SpendRow[];
}
export async function canManageSpend(): Promise<boolean> {
  const { data } = await getSupabaseClient().rpc("capability_level", { p_cap: "manage_campaigns_automations" });
  return data === "full";
}
// month arrives as "YYYY-MM" (month picker) -> normalize to the first of the month.
export async function saveSpend(month: string, amount: number, note: string, id?: string) {
  const period_month = `${month}-01`;
  const sb = getSupabaseClient();
  if (id) return sb.from("marketing_spend").update({ amount, note: note || null }).eq("id", id);
  return sb.from("marketing_spend").upsert({ period_month, amount, note: note || null }, { onConflict: "period_month" });
}
export async function deleteSpend(id: string) {
  return getSupabaseClient().from("marketing_spend").delete().eq("id", id);
}

// client-side CSV export
export function downloadCsv(filename: string, rows: Record<string, any>[]) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const esc = (v: any) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
